<template>
  <section class="section" id="iletisim">
    <div class="container">
      <div class="cta-band">
        <div>
          <h2>{{ t("cta.title") }}</h2>
          <p>{{ t("cta.lead") }}</p>
        </div>
        <a class="btn btn--primary" :href="quoteHref" target="_blank" rel="noopener">
          {{ t("contact.send") }} <span aria-hidden="true">→</span>
        </a>
      </div>
      <div class="contact-grid">
        <div class="contact-card">
          <h3>{{ t("contact.title") }}</h3>
          <p class="contact-lead">{{ t("contact.lead") }}</p>

          <div class="contact-channels">
            <a class="contact-channel" :href="`tel:${company.phoneTel}`">
              <div class="contact-channel__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
              </div>
              <div class="contact-channel__meta">
                <div class="contact-channel__top">
                  <span class="contact-channel__label">{{ t("contact.phone") }}</span>
                  <span class="contact-channel__action">{{ t("contact.callCta") }} <span aria-hidden="true">→</span></span>
                </div>
                <span class="contact-channel__value">{{ company.phone }}</span>
              </div>
            </a>

            <a class="contact-channel contact-channel--whatsapp" :href="quoteHref" target="_blank" rel="noopener">
              <div class="contact-channel__icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.5 3.5A11 11 0 0 0 2.1 17.6L1 23l5.6-1.1A11 11 0 0 0 20.5 3.5zm-8.5 17a9.1 9.1 0 0 1-4.6-1.3l-.3-.2-3.3.7.7-3.2-.2-.3A9.1 9.1 0 1 1 12 20.5zm5-6.8c-.3-.1-1.6-.8-1.9-.9s-.4-.1-.6.1-.7.9-.9 1.1-.3.2-.6.1a7.4 7.4 0 0 1-2.2-1.4 8 8 0 0 1-1.5-1.9c-.2-.3 0-.4.1-.6l.5-.6c.1-.2.1-.3 0-.5l-.9-2.1c-.2-.6-.5-.5-.6-.5h-.5c-.2 0-.5.1-.8.4s-1 1-1 2.5 1.1 2.9 1.2 3.1a12.3 12.3 0 0 0 4.2 3.7c.6.3 1 .5 1.4.6.6.3 1.1.2 1.5.1.5-.1 1.6-.7 1.8-1.3s.2-1.2.2-1.3-.1-.2-.4-.3z"/>
                </svg>
              </div>
              <div class="contact-channel__meta">
                <div class="contact-channel__top">
                  <span class="contact-channel__label">{{ t("contact.whatsapp") }}</span>
                  <span class="contact-channel__tag contact-channel__tag--online">
                    <span class="contact-channel__live-dot"></span> Canlı Hat
                  </span>
                </div>
                <div class="contact-channel__btn-action">
                  <span>{{ t("contact.waBtn") }}</span>
                  <span aria-hidden="true">→</span>
                </div>
              </div>
            </a>

            <a
              v-for="mail in company.emails"
              :key="mail"
              class="contact-channel"
              :href="`mailto:${mail}`"
            >
              <div class="contact-channel__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <div class="contact-channel__meta">
                <div class="contact-channel__top">
                  <span class="contact-channel__label">{{ t("contact.email") }}</span>
                  <span class="contact-channel__action">{{ t("contact.emailCta") }} <span aria-hidden="true">→</span></span>
                </div>
                <span class="contact-channel__value">{{ mail }}</span>
              </div>
            </a>

            <div class="contact-channel contact-channel--static">
              <div class="contact-channel__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
              <div class="contact-channel__meta">
                <div class="contact-channel__top">
                  <span class="contact-channel__label">{{ t("contact.office") }}</span>
                  <span class="contact-channel__tag">{{ t("contact.officeTag") }}</span>
                </div>
                <span class="contact-channel__value">{{ company.city }}</span>
              </div>
            </div>
          </div>
        </div>
        <form class="form" @submit.prevent="send">
          <p v-if="error" class="error">{{ t("contact.required") }}</p>
          <p v-if="status === 'ok'" class="success">{{ t("contact.success") }}</p>
          <p v-if="status === 'fail'" class="error">{{ t("contact.fail") }}</p>
          <label for="name">{{ t("contact.name") }}</label>
          <input id="name" v-model.trim="name" type="text" autocomplete="name" required />
          <label for="email">{{ t("contact.email") }}</label>
          <input id="email" v-model.trim="email" type="email" autocomplete="email" required />
          <label class="form__honeypot" for="website">Website</label>
          <input id="website" v-model="website" class="form__honeypot" tabindex="-1" autocomplete="off" />
          <label for="message">{{ t("contact.message") }}</label>
          <textarea id="message" v-model.trim="message" required></textarea>
          <button class="btn btn--primary btn--block" type="submit" :disabled="sending">
            {{ sending ? t("contact.sending") : t("contact.sendWa") }}
            <span v-if="!sending" aria-hidden="true">→</span>
          </button>
          <p class="form__hint">{{ t("contact.formHint") }}</p>
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { company, whatsappHref } from "@/data/company";

export default {
  name: "ContactSection",
  setup() {
    const { t, locale } = useI18n();
    const name = ref("");
    const email = ref("");
    const website = ref("");
    const message = ref("");
    const error = ref(false);
    const sending = ref(false);
    const status = ref("");
    const quoteHref = computed(() => whatsappHref(t("contact.waPrefill")));

    async function send() {
      if (!name.value || !email.value || !message.value) {
        error.value = true;
        status.value = "";
        return;
      }
      error.value = false;
      sending.value = true;
      status.value = "";
      try {
        const res = await fetch("/api/contact", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: name.value,
            email: email.value,
            message: message.value,
            website: website.value,
            locale: locale.value,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.ok) {
          throw new Error("mail");
        }
        status.value = "ok";
        name.value = "";
        email.value = "";
        message.value = "";
      } catch {
        status.value = "fail";
      } finally {
        sending.value = false;
      }
    }

    return {
      t,
      company,
      name,
      email,
      website,
      message,
      error,
      sending,
      status,
      quoteHref,
      send,
    };
  },
};
</script>
